import { Controller } from "@hotwired/stimulus";
import { type Played, playCard } from "../dice/card";
import { createRollWatch, isOwnRoll, rollData } from "../dice/feed";
import { flipSchedule, instantSchedule, rollKey } from "../dice/landing";
import { reducedMotion } from "../dice/preferences";

const ROLL = ".odo-dice-roll[id]";

// Connects to data-controller="dice-feed" on the campaign's dice_rolls frame,
// on its page and in the site's drawer. Each roll its stream brings flips
// once; what the page or a frame load shows is history. Inside a board's feed
// frame map-dice plays the rolls, so this stays inert. An entry playing as
// Turbo caches the page is cached settled. `handle` is the viewer's, so a
// roll of theirs from another device plays as their own.
export default class extends Controller {
	static values = { handle: String };
	declare readonly handleValue: string;
	watch: ReturnType<typeof createRollWatch>;
	observer?: MutationObserver;
	plays = new Map<HTMLElement, Played>();
	rendering = false;

	connect() {
		if (this.element.closest('[data-map-dice-target="frame"]')) return;
		this.watch = createRollWatch();
		this.watch.prime(this.entries().map(({ id }) => id));
		this.observer = new MutationObserver(() => this.announceNew());
		this.observer.observe(this.element, { childList: true, subtree: true });
		this.element.addEventListener("turbo:before-frame-render", this.render);
		this.element.addEventListener("turbo:frame-load", this.loaded);
		document.addEventListener("visibilitychange", this.hidden);
		document.addEventListener("turbo:before-cache", this.settle);
	}

	disconnect() {
		if (!this.observer) return;
		this.observer.disconnect();
		this.observer = undefined;
		this.element.removeEventListener("turbo:before-frame-render", this.render);
		this.element.removeEventListener("turbo:frame-load", this.loaded);
		document.removeEventListener("visibilitychange", this.hidden);
		document.removeEventListener("turbo:before-cache", this.settle);
		this.settle();
	}

	entries() {
		return [...this.element.querySelectorAll<HTMLElement>(ROLL)];
	}

	// Turbo renders a frame's contents, which the observer hears, and repaints
	// before turbo:frame-load: what arrives in between is the load's.
	render = () => {
		this.rendering = true;
	};

	loaded = () => {
		this.observer?.takeRecords();
		this.watch.prime(this.entries().map(({ id }) => id));
		this.rendering = false;
	};

	announceNew() {
		if (!this.observer || this.rendering) return;
		const entries = this.entries();
		const fresh = new Set(this.watch.fresh(entries.map(({ id }) => id)));
		if (document.hidden) return;
		for (const element of entries) {
			const data = fresh.has(element.id) && rollData(element);
			if (data) this.play(element, rollKey(data), isOwnRoll(data, this.handleValue));
		}
	}

	play(element: HTMLElement, key: string, own: boolean) {
		const schedule = reducedMotion() ? instantSchedule() : flipSchedule(element.querySelectorAll("[data-die-tile]").length);
		element.addEventListener("dice-landing:done", () => this.plays.delete(element), { once: true });
		const played = playCard(element, schedule, { key, own });
		if (element.dataset.state === "playing") this.plays.set(element, played);
	}

	settle = () => {
		for (const played of this.plays.values()) played.cancel();
		this.plays.clear();
	};

	hidden = () => {
		if (document.hidden) for (const played of [...this.plays.values()]) played.skip();
	};
}
